import React, { useState, useEffect, useRef } from 'react';
import { getMediaServeUrl } from '../../config/mediaAPI';

const CapsuleViewer = ({ capsule, onClose, onMarkViewed, onDeliver, videoThumbnails = {} }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [selectedReaction, setSelectedReaction] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const hasMarkedViewed = useRef(false);

  // Auto-mark as viewed when opening an unread received letter (fire once, skip previews)
  useEffect(() => {
    if (capsule && capsule.viewStatus === 'ready' && !capsule.isPreview && onMarkViewed && !hasMarkedViewed.current) {
      hasMarkedViewed.current = true;
      onMarkViewed(capsule.id);
    }
  }, [capsule, onMarkViewed]);

  if (!capsule) return null;

  const isReceivedLetter = capsule.isPreview || capsule.viewStatus === 'ready' || capsule.viewStatus === 'read';

  const mediaFiles = capsule.media_files_detail || [];
  const images = mediaFiles.filter(m => m.media_type === 'image');
  const videos = mediaFiles.filter(m => m.media_type === 'video');
  const audios = mediaFiles.filter(m => m.media_type === 'audio');
  const hasMedia = mediaFiles.length > 0;

  // Get media type icon
  const getMediaIcon = (mediaType) => {
    switch (mediaType) {
      case 'image': return '📷';
      case 'video': return '🎥';
      case 'audio': return '🎵';
      default: return '📄';
    }
  };

  // Open media in lightbox
  const openMedia = (media, index) => {
    setSelectedMedia(media);
    setCurrentMediaIndex(index);
  };

  // Navigate media in lightbox
  const navigateMedia = (direction) => {
    const newIndex = currentMediaIndex + direction;
    if (newIndex >= 0 && newIndex < mediaFiles.length) {
      setCurrentMediaIndex(newIndex);
      setSelectedMedia(mediaFiles[newIndex]);
    }
  };

  // Close lightbox
  const closeLightbox = () => {
    setSelectedMedia(null);
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Sender info
  const senderFirstName = capsule.sender_first_name || '';
  const senderLastName = capsule.sender_last_name || '';
  const senderName = `${senderFirstName} ${senderLastName}`.trim() || 'Someone';
  const senderInitials = `${senderFirstName.charAt(0)}${senderLastName.charAt(0)}`.toUpperCase();

  // Hero image (first image, or null for gradient placeholder)
  const heroImage = images.length > 0 ? getMediaServeUrl(images[0].id) : null;
  // Gallery images (after hero)
  const galleryImages = images.length > 1 ? images.slice(1) : [];

  // Title
  const letterTitle = capsule.title || `A Letter from ${senderFirstName || 'Someone'}`;

  // Milestone
  const milestoneName = capsule.milestone_name || capsule.delivery_event || '';

  // Reactions config
  const reactions = [
    { key: 'moved', emoji: '🥹', label: 'Moved' },
    { key: 'love', emoji: '❤️', label: 'Love' },
    { key: 'grateful', emoji: '😊', label: 'Grateful' },
    { key: 'miss', emoji: '😢', label: 'Miss You' },
  ];

  // ─── Immersive View for Received Letters ───
  if (isReceivedLetter) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&display=swap');

          @keyframes immersiveHeroReveal {
            0% { opacity: 0; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes immersiveFadeUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }

          .immersive-hero-img {
            opacity: 0;
            animation: immersiveHeroReveal 2s ease-out 0.3s forwards;
          }
          .immersive-hero-content {
            opacity: 0;
            animation: immersiveFadeUp 1s ease-out 0.8s forwards;
          }
          .immersive-video-section {
            opacity: 0;
            animation: immersiveFadeUp 0.8s ease-out 1.2s forwards;
          }
          .immersive-photos-section {
            opacity: 0;
            animation: immersiveFadeUp 0.8s ease-out 1.5s forwards;
          }
          .immersive-letter-text {
            opacity: 0;
            animation: immersiveFadeUp 0.8s ease-out 1.8s forwards;
          }
          .immersive-response-area {
            opacity: 0;
            animation: immersiveFadeUp 0.8s ease-out 2.2s forwards;
          }

          .immersive-letter-content {
            font-family: 'Lora', serif;
            font-size: 18px;
            line-height: 1.85;
            color: #3D3426;
          }
          .immersive-letter-content p {
            margin-bottom: 28px;
          }
          .immersive-letter-content p:first-of-type::first-letter {
            font-size: 52px;
            float: left;
            line-height: 1;
            margin-right: 8px;
            margin-top: 4px;
            font-weight: 700;
            color: #556B2F;
          }
        `}</style>

        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: '#2C2416' }}>

          {/* ─── Top Bar ─── */}
          <div
            className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-14"
            style={{
              background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%)',
              pointerEvents: 'none',
            }}
          >
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg cursor-pointer transition-colors"
              style={{
                color: 'rgba(255,255,255,0.85)',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.08)',
                pointerEvents: 'auto',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {capsule.isPreview ? 'Back to Letter' : 'My Treasures'}
            </button>

            <div className="flex items-center gap-2" style={{ pointerEvents: 'auto' }}>
              {/* Deliver Now - only in preview mode (sender's view) and not yet delivered */}
              {capsule.isPreview && capsule.delivery_status !== 'DELIVERED' && capsule.delivery_status !== 'VIEWED' && (
                <button
                  onClick={() => onDeliver && onDeliver(capsule)}
                  className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg cursor-pointer transition-colors"
                  style={{
                    background: '#E65100',
                    color: '#FFFFFF',
                    border: 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#BF4500'}
                  onMouseLeave={e => e.currentTarget.style.background = '#E65100'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  Deliver Now
                </button>
              )}
              {/* Share */}
              <button
                className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.85)',
                }}
                title="Share with family"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              </button>
              {/* Download */}
              <button
                className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.85)',
                }}
                title="Download"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
              {/* Print */}
              <button
                className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.85)',
                }}
                title="Print"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              </button>
            </div>
          </div>

          {/* ─── Hero Section ─── */}
          <section className="relative w-full flex items-end overflow-hidden" style={{ minHeight: '70vh', background: '#2C2416' }}>
            {/* Hero background */}
            <div className="absolute inset-0">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt=""
                  className="w-full h-full object-cover immersive-hero-img"
                />
              ) : (
                <div
                  className="w-full h-full immersive-hero-img"
                  style={{
                    background: 'linear-gradient(135deg, #8B7355 0%, #A0936B 30%, #C4B896 60%, #D4C9A8 100%)',
                  }}
                />
              )}
            </div>

            {/* Vignette overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `
                  linear-gradient(180deg, rgba(0,0,0,0.15) 0%, transparent 30%, transparent 50%, rgba(44,36,22,0.7) 80%, rgba(44,36,22,0.95) 100%),
                  radial-gradient(ellipse at center, transparent 40%, rgba(44,36,22,0.3) 100%)
                `,
              }}
            />

            {/* Hero content */}
            <div className="relative z-[2] w-full max-w-[680px] mx-auto px-8 pb-12 immersive-hero-content">
              {milestoneName && (
                <div
                  className="inline-flex items-center gap-1.5 mb-3.5"
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    color: 'rgba(255,255,255,0.6)',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                  {capsule.milestone_icon || ''} {milestoneName}
                </div>
              )}

              <h1
                className="mb-4"
                style={{
                  fontFamily: "'Lora', serif",
                  fontSize: '36px',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  lineHeight: 1.2,
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                }}
              >
                {letterTitle}
              </h1>

              <div className="flex items-center gap-3.5">
                <div
                  className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
                  style={{
                    border: '2.5px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                  }}
                >
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{
                      background: '#556B2F',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '18px',
                    }}
                  >
                    {senderInitials || '?'}
                  </div>
                </div>
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '16px', fontWeight: 600, color: '#FFFFFF' }}>
                    {senderName}
                  </div>
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                    {capsule.created_at ? `Written ${formatDate(capsule.created_at)}` : ''}
                    {capsule.created_at && capsule.delivery_datetime ? ' · ' : ''}
                    {capsule.delivery_datetime ? `Delivered ${formatDate(capsule.delivery_datetime)}` : ''}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ─── Transition ─── */}
          <div style={{ background: 'linear-gradient(180deg, #2C2416 0%, #F6F0E4 100%)', height: '80px' }} />

          {/* ─── Letter Body ─── */}
          <div className="relative" style={{ background: '#F6F0E4' }}>
            {/* Subtle paper texture overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `
                  radial-gradient(ellipse at 20% 50%, rgba(200,180,140,0.15) 0%, transparent 50%),
                  radial-gradient(ellipse at 80% 30%, rgba(200,180,140,0.1) 0%, transparent 50%)
                `,
              }}
            />

            <div className="relative z-[1] max-w-[620px] mx-auto px-8 pb-16">

              {/* ─── Video Section ─── */}
              {videos.length > 0 && videos.map((video) => {
                const videoUrl = video.file_url_debug || video.file;
                const thumbUrl = videoThumbnails[video.id] || video.thumbnail;
                const isPlaying = playingVideoId === video.id;

                return (
                  <div key={video.id} className="mb-12 immersive-video-section">
                    <div
                      className="flex items-center gap-2 mb-3.5"
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: '#999999',
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                      Video message from {senderFirstName || 'sender'}
                    </div>
                    <div
                      className="w-full rounded-2xl overflow-hidden relative"
                      style={{
                        aspectRatio: '16/9',
                        background: '#1a1a1a',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                      }}
                    >
                      {isPlaying ? (
                        <video
                          src={videoUrl}
                          controls
                          autoPlay
                          className="w-full h-full object-contain"
                          style={{ background: '#000' }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <div
                          className="w-full h-full cursor-pointer"
                          onClick={() => setPlayingVideoId(video.id)}
                        >
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt="Video thumbnail"
                              className="w-full h-full object-cover"
                              style={{ filter: 'brightness(0.85)' }}
                            />
                          ) : (
                            <div className="w-full h-full" style={{ background: 'linear-gradient(145deg, #5D5040 0%, #8B7355 50%, #A08868 100%)' }} />
                          )}
                          {/* Play button overlay */}
                          <div
                            className="absolute top-1/2 left-1/2 flex items-center justify-center"
                            style={{
                              transform: 'translate(-50%, -50%)',
                              width: '72px',
                              height: '72px',
                              borderRadius: '50%',
                              background: 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(12px)',
                              border: '2px solid rgba(255,255,255,0.3)',
                            }}
                          >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          </div>
                        </div>
                      )}
                    </div>
                    {video.title && (
                      <div
                        className="text-center mt-3.5"
                        style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '14px', color: '#666666' }}
                      >
                        {video.title}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ─── Photo Gallery ─── */}
              {galleryImages.length > 0 && (
                <div className="mb-12 immersive-photos-section">
                  <div
                    className="grid gap-2.5"
                    style={{ gridTemplateColumns: '1fr 1fr' }}
                  >
                    {galleryImages.map((img, idx) => (
                      <div
                        key={img.id}
                        className="rounded-xl overflow-hidden relative cursor-pointer transition-transform hover:scale-[1.02]"
                        style={{
                          gridColumn: idx === 0 ? '1 / -1' : 'auto',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        }}
                        onClick={() => openMedia(img, mediaFiles.indexOf(img))}
                      >
                        <img
                          src={getMediaServeUrl(img.id)}
                          alt={img.title || ''}
                          className="w-full block object-cover"
                          style={{ aspectRatio: idx === 0 ? '16/9' : '4/3' }}
                        />
                        {img.title && (
                          <div
                            className="absolute bottom-0 left-0 right-0 px-4 pt-6 pb-3"
                            style={{
                              background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
                              fontFamily: "'Lora', serif",
                              fontSize: '13px',
                              fontStyle: 'italic',
                              color: '#FFFFFF',
                            }}
                          >
                            {img.title}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ─── Audio Section ─── */}
              {audios.length > 0 && (
                <div className="mb-12 immersive-video-section">
                  <div
                    className="flex items-center gap-2 mb-3.5"
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      color: '#999999',
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                    Audio message
                  </div>
                  {audios.map((audio) => (
                    <div key={audio.id} className="mb-4">
                      <audio
                        src={audio.file_url_debug || audio.file}
                        controls
                        className="w-full"
                        style={{ borderRadius: '14px' }}
                      >
                        Your browser does not support the audio tag.
                      </audio>
                      {audio.title && (
                        <div
                          className="text-center mt-2"
                          style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '14px', color: '#666666' }}
                        >
                          {audio.title}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* ─── Letter Text ─── */}
              <div className="immersive-letter-text">
                {/* Written date */}
                {capsule.created_at && (
                  <div className="flex items-center gap-2 mb-2" style={{ fontSize: '13px', color: '#999999' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    Written on {formatDate(capsule.created_at)}
                  </div>
                )}

                {/* Decorative divider */}
                <div className="flex items-center gap-4 mb-9">
                  <div className="flex-1 h-px" style={{ background: '#E8DCC8' }} />
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: '#E8DCC8' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div className="flex-1 h-px" style={{ background: '#E8DCC8' }} />
                </div>

                {/* Letter content */}
                <div
                  className="immersive-letter-content"
                  dangerouslySetInnerHTML={{ __html: capsule.message }}
                />

                {/* ─── Response Area (hidden in preview mode) ─── */}
                {!capsule.isPreview && <div className="immersive-response-area" style={{ marginTop: '56px', paddingTop: '40px', borderTop: '1px solid #E8DCC8' }}>
                  {/* Prompt */}
                  <div className="text-center mb-7">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3.5"
                      style={{ background: 'rgba(85,107,47,0.08)' }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#556B2F" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                    </div>
                    <h3 style={{ fontFamily: "'Lora', serif", fontSize: '18px', fontWeight: 600, color: '#333333', marginBottom: '4px' }}>
                      How does this make you feel?
                    </h3>
                    <p style={{ fontSize: '14px', color: '#666666' }}>
                      Your reaction will be saved as part of your family's story.
                    </p>
                  </div>

                  {/* Reaction buttons */}
                  <div className="flex justify-center gap-3 mb-7">
                    {reactions.map(r => (
                      <button
                        key={r.key}
                        className="flex flex-col items-center gap-1.5 rounded-2xl cursor-pointer transition-all"
                        style={{
                          padding: '14px 20px',
                          minWidth: '80px',
                          border: selectedReaction === r.key
                            ? '1.5px solid #556B2F'
                            : '1.5px solid #E8DCC8',
                          background: selectedReaction === r.key
                            ? '#F1F8E9'
                            : '#FAF8F0',
                          transform: selectedReaction === r.key ? 'translateY(-2px)' : 'none',
                          boxShadow: selectedReaction === r.key ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
                        }}
                        onClick={() => setSelectedReaction(selectedReaction === r.key ? null : r.key)}
                      >
                        <span style={{ fontSize: '24px' }}>{r.emoji}</span>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#666666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {r.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Write response */}
                  <div
                    className="rounded-2xl mb-5"
                    style={{
                      background: '#FAF8F0',
                      border: '1.5px solid #E8DCC8',
                      padding: '20px',
                    }}
                  >
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '14px', fontWeight: 600, color: '#333333', marginBottom: '10px' }}>
                      Write a response to {senderFirstName || 'sender'}
                    </div>
                    <textarea
                      value={responseText}
                      onChange={e => setResponseText(e.target.value)}
                      placeholder="What would you say back? This is just for you and your family..."
                      className="w-full resize-y"
                      style={{
                        minHeight: '100px',
                        padding: '14px',
                        border: '1px solid #E8DCC8',
                        borderRadius: '10px',
                        fontFamily: "'Lora', serif",
                        fontSize: '15px',
                        lineHeight: 1.7,
                        color: '#333333',
                        background: '#FFFFFF',
                        outline: 'none',
                      }}
                      onFocus={e => e.target.style.borderColor = '#556B2F'}
                      onBlur={e => e.target.style.borderColor = '#E8DCC8'}
                    />
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex gap-1.5">
                        {/* Attach photo */}
                        <button
                          className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                          style={{ border: '1px solid #E8DCC8', background: '#FFFFFF', color: '#666666' }}
                          title="Attach photo"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        </button>
                        {/* Record voice */}
                        <button
                          className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                          style={{ border: '1px solid #E8DCC8', background: '#FFFFFF', color: '#666666' }}
                          title="Record voice"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                        </button>
                        {/* Record video */}
                        <button
                          className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                          style={{ border: '1px solid #E8DCC8', background: '#FFFFFF', color: '#666666' }}
                          title="Record video"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                        </button>
                      </div>
                      <button
                        className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg cursor-pointer transition-colors"
                        style={{
                          padding: '9px 20px',
                          border: 'none',
                          background: '#556B2F',
                          color: '#FFFFFF',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#4A5D23'}
                        onMouseLeave={e => e.currentTarget.style.background = '#556B2F'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                        Save Response
                      </button>
                    </div>
                  </div>

                  {/* Bottom actions */}
                  <div className="flex justify-center gap-2.5 pt-3">
                    <button
                      className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-xl cursor-pointer transition-colors"
                      style={{ padding: '10px 18px', border: '1.5px solid #E8DCC8', background: '#FAF8F0', color: '#666666' }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                      Share with Family
                    </button>
                    <button
                      className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-xl cursor-pointer transition-colors"
                      style={{ padding: '10px 18px', border: '1.5px solid #E8DCC8', background: '#FAF8F0', color: '#666666' }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Letter
                    </button>
                    <button
                      className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-xl cursor-pointer transition-colors"
                      style={{ padding: '10px 18px', border: '1.5px solid #E8DCC8', background: '#FAF8F0', color: '#666666' }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                      Print
                    </button>
                  </div>
                </div>}
              </div>
            </div>
          </div>

          {/* ─── Footer ─── */}
          <div className="text-center" style={{ background: '#F6F0E4', padding: '40px 32px' }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: '#556B2F', opacity: 0.4 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <div style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '13px', color: '#999999' }}>
              Preserved with love by One Generation to Next
            </div>
          </div>
        </div>

        {/* ─── Media Lightbox (shared) ─── */}
        {selectedMedia && (
          <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 z-10"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {currentMediaIndex > 0 && (
              <button
                onClick={() => navigateMedia(-1)}
                className="absolute left-4 text-white hover:bg-white/20 rounded-full p-2"
              >
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {currentMediaIndex < mediaFiles.length - 1 && (
              <button
                onClick={() => navigateMedia(1)}
                className="absolute right-4 text-white hover:bg-white/20 rounded-full p-2"
              >
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div className="max-w-5xl max-h-[85vh] w-full mx-4">
              {selectedMedia.media_type === 'image' ? (
                <img
                  src={getMediaServeUrl(selectedMedia.id)}
                  alt={selectedMedia.title}
                  className="max-w-full max-h-[85vh] mx-auto object-contain"
                />
              ) : selectedMedia.media_type === 'video' ? (
                <video
                  src={selectedMedia.file_url_debug || selectedMedia.file}
                  controls
                  autoPlay
                  className="max-w-full max-h-[85vh] mx-auto"
                >
                  Your browser does not support the video tag.
                </video>
              ) : selectedMedia.media_type === 'audio' ? (
                <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-auto">
                  <div className="text-6xl text-center mb-4">🎵</div>
                  <p className="text-white text-center mb-4">{selectedMedia.title}</p>
                  <audio
                    src={selectedMedia.file_url_debug || selectedMedia.file}
                    controls
                    autoPlay
                    className="w-full"
                  >
                    Your browser does not support the audio tag.
                  </audio>
                </div>
              ) : (
                <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-auto text-center">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-white">{selectedMedia.title}</p>
                  <a
                    href={selectedMedia.file_url_debug || selectedMedia.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary"
                  >
                    Download File
                  </a>
                </div>
              )}
            </div>

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              {currentMediaIndex + 1} / {mediaFiles.length}
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Original Modal for Own Letters / Previews ───
  return (
    <>
      {/* Main Viewer Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-primary text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {capsule.title || 'Time Capsule'}
              </h2>
              <p className="text-sm opacity-90">
                {capsule.isMyLetter ? (
                  <>To: {capsule.receiver_first_name} {capsule.receiver_last_name || ''}</>
                ) : (
                  <>From: {capsule.sender_first_name} {capsule.sender_last_name}</>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Delivery Info */}
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500 flex-wrap">
              {capsule.isMyLetter ? (
                <span>
                  {capsule.delivery_status === 'DRAFT' ? 'Draft - ' :
                   capsule.delivery_status === 'SCHEDULED' ? 'Scheduled for ' : 'Delivered on '}
                  {capsule.delivery_datetime ? new Date(capsule.delivery_datetime).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'date not set'}
                </span>
              ) : (
                <span>Delivered on {new Date(capsule.delivery_datetime).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              )}
              {capsule.milestone_name && (
                <span className="bg-primary/10 text-primary px-2 py-1 rounded-full">
                  {capsule.milestone_icon} {capsule.milestone_name}
                </span>
              )}
              {capsule.isMyLetter && capsule.status && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  capsule.status === 'DRAFT' ? 'bg-orange-100 text-orange-700' :
                  capsule.status === 'SCHEDULED' ? 'bg-green-100 text-green-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {capsule.status}
                </span>
              )}
            </div>

            {/* Letter Content */}
            <div className="prose max-w-none mb-6">
              <div
                dangerouslySetInnerHTML={{ __html: capsule.message }}
                className="text-lg leading-relaxed"
              />
            </div>

            {/* Media Section */}
            {hasMedia && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>📎</span>
                  <span>Attached Media ({mediaFiles.length})</span>
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {mediaFiles.map((media, index) => (
                    <div
                      key={media.id}
                      onClick={() => openMedia(media, index)}
                      className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all group"
                    >
                      {media.media_type === 'image' ? (
                        <img
                          src={getMediaServeUrl(media.id)}
                          alt={media.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                          <span className="text-4xl">{getMediaIcon(media.media_type)}</span>
                          <span className="text-xs text-gray-500 mt-2 px-2 truncate max-w-full">
                            {media.title}
                          </span>
                        </div>
                      )}

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium">
                          View
                        </span>
                      </div>

                      {/* Media type badge */}
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/50 text-white text-xs rounded">
                        {getMediaIcon(media.media_type)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 flex justify-between items-center bg-gray-50">
            <div className="text-sm text-gray-500">
              {capsule.isMyLetter ? (
                <div>
                  <span className="text-gray-600">📝 Your Letter</span>
                  {(capsule.delivery_status === 'DELIVERED' || capsule.delivery_status === 'VIEWED') && capsule.delivery_datetime && (
                    <span className="ml-2 text-xs text-gray-400">
                      Delivered {new Date(capsule.delivery_datetime).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              ) : capsule.delivery_status === 'VIEWED' ? (
                <span className="text-green-600">Viewed</span>
              ) : capsule.delivery_status === 'DELIVERED' ? (
                <span className="text-blue-600">New</span>
              ) : (
                <span>{capsule.delivery_status}</span>
              )}
            </div>
            <div className="flex gap-3">
              {capsule.isMyLetter && capsule.delivery_status !== 'DELIVERED' && capsule.delivery_status !== 'VIEWED' && (
                <button
                  onClick={() => onDeliver && onDeliver(capsule)}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
                  style={{ background: '#E65100' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  Deliver Now
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Media Lightbox */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 z-10"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Navigation arrows */}
          {currentMediaIndex > 0 && (
            <button
              onClick={() => navigateMedia(-1)}
              className="absolute left-4 text-white hover:bg-white/20 rounded-full p-2"
            >
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {currentMediaIndex < mediaFiles.length - 1 && (
            <button
              onClick={() => navigateMedia(1)}
              className="absolute right-4 text-white hover:bg-white/20 rounded-full p-2"
            >
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Media content */}
          <div className="max-w-5xl max-h-[85vh] w-full mx-4">
            {selectedMedia.media_type === 'image' ? (
              <img
                src={getMediaServeUrl(selectedMedia.id)}
                alt={selectedMedia.title}
                className="max-w-full max-h-[85vh] mx-auto object-contain"
              />
            ) : selectedMedia.media_type === 'video' ? (
              <video
                src={selectedMedia.file_url_debug || selectedMedia.file}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] mx-auto"
              >
                Your browser does not support the video tag.
              </video>
            ) : selectedMedia.media_type === 'audio' ? (
              <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-auto">
                <div className="text-6xl text-center mb-4">🎵</div>
                <p className="text-white text-center mb-4">{selectedMedia.title}</p>
                <audio
                  src={selectedMedia.file_url_debug || selectedMedia.file}
                  controls
                  autoPlay
                  className="w-full"
                >
                  Your browser does not support the audio tag.
                </audio>
              </div>
            ) : (
              <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-auto text-center">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-white">{selectedMedia.title}</p>
                <a
                  href={selectedMedia.file_url_debug || selectedMedia.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary"
                >
                  Download File
                </a>
              </div>
            )}
          </div>

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
            {currentMediaIndex + 1} / {mediaFiles.length}
          </div>
        </div>
      )}
    </>
  );
};

export default CapsuleViewer;
